import { ChatUserAction, useNavigationState } from "@/zustand/NavigationState";
import { isArchitectChat } from "./architectChat";

/**
 * Is the composer the user is attaching from an ORION Architect one?
 *
 * Two shapes to cover: an open Architect conversation, and the new-AI-chat screen
 * where no chat exists yet (`activeChatId` null, hub id not yet known) so the
 * navigation action is the only discriminator.
 *
 * The `activeChatId === null` branch order matters — but not because opening a
 * chat leaves the action stale: `setActiveChatId` clears `activeChatUserAction`
 * to null (NavigationState). It matters for two edge states where the action
 * says CREATE_AI_CHAT while a chat id may be present: the initial store value,
 * and `useNewAIChatVoice` setting `activeChatId` directly during recording
 * without clearing the action. Reading `activeChatId` first keeps both from
 * applying the Architect cap in ordinary chats.
 *
 * Used at STAGING time only, for immediate feedback when a file is picked. The
 * authoritative check runs at send time against the destination chat record (see
 * `middleware/messages.ts`), which is what makes the limit immune to staging a
 * file in one chat and sending it in another.
 *
 * Kept out of `architectChat.ts` so that module stays free of store imports —
 * middleware and its pure unit test both depend on it.
 */
export const isArchitectComposerActive = (): boolean => {
  const nav = useNavigationState.getState();
  return nav.activeChatId === null
    ? nav.activeChatUserAction === ChatUserAction.CREATE_AI_CHAT
    : isArchitectChat(nav.activeChatType, nav.activeChatHubId);
};
